import { DestroyRef, inject, Service } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import type { OnyxHttpErrorResponse } from '@onyx/angular';
import { DateTime, Duration } from 'luxon';
import type { Observable } from 'rxjs';
import {
  catchError,
  EMPTY,
  map,
  of,
  Subject,
  switchMap,
  takeUntil,
  tap,
  timer,
} from 'rxjs';
import { environment } from '../../../../config/environment';
import type { BusinessDocuments } from '../../../common/interfaces/documents/business-documents';
import { ApiService } from '../../../common/services/api.service';
import { StorageService } from '../../../common/services/storage.service';
import type { Business } from '../../../dashboard/management-panel/business/common/interfaces/business';
import type { DocumentsForm } from '../../../dashboard/management-panel/documents/common/interfaces/documents-form';
import type { ForgotPasswordForm } from '../../forgot-password/forgot-password.component';
import type { JoinForm } from '../../join/join.component';
import type { LoginForm } from '../../login/login.component';
import type { RegisterCompanyForm } from '../../register-company/register-company.component';
import type { RegisterFormDto } from '../../register/register.component';
import type { ResetPasswordForm } from '../../reset-password/reset-password.component';
import { AUTH_INTERCEPTOR_NAMESPACE_HEADER } from '../constants/auth-interceptor-namespace-header';
import { AuthRoute } from '../enums/auth-route';
import type { BusinessDocumentType } from '../enums/business/business-document-type';
import { AuthStore } from '../stores/auth.store';

@Service()
export class AuthService extends ApiService {
  private readonly store = inject(AuthStore);
  private readonly router = inject(Router);
  private readonly storageService = inject(StorageService);
  private readonly destroyRef = inject(DestroyRef);

  private refreshToken$ = new Subject<void>();

  public login(form: LoginForm): Observable<void> {
    const { companyId, ...body } = form;

    const login$ = (companyId: string | null) =>
      this.post<{ token: string }>('/login', body, {
        headers: {
          [AUTH_INTERCEPTOR_NAMESPACE_HEADER]:
            companyId ?? environment.publicApiNamespace,
        },
        withCredentials: true,
      });

    return login$(companyId).pipe(
      catchError((response: OnyxHttpErrorResponse) => {
        if (response.status === 0) return login$(null);
        throw response;
      }),
      tap(({ token }) => {
        const { exp } = this.store.save(token, { login: true });
        this.scheduleRefreshToken(exp);
      }),
      map(() => undefined),
    );
  }

  public refreshToken(): Observable<string> {
    return this.post<{ token: string }>('/token/refresh', undefined, {
      withCredentials: true,
    }).pipe(
      map(({ token }) => token),
      tap((token) => {
        const { exp } = this.store.save(token, { login: false });
        this.scheduleRefreshToken(exp);
      }),
    );
  }

  public logout({ redirect }: { redirect: boolean }): void {
    this.store.logout();
    if (redirect) this.router.navigateByUrl(AuthRoute.LOGIN);

    this.post<void>('/token/invalidate', undefined, {
      withCredentials: true,
    })
      .pipe(catchError(() => of(undefined)))
      .subscribe();
  }

  public initialize(): Observable<void> {
    if (this.store.initialized()) return of(undefined);
    if (!this.store.companyId()) {
      this.store.initialize();
      return of(undefined);
    }

    return this.refreshToken().pipe(
      catchError(() => of(undefined)),
      tap(() => this.store.initialize()),
      map(() => undefined),
    );
  }

  public register(form: RegisterFormDto): Observable<void> {
    return this.post('/register', form, {
      headers: {
        [AUTH_INTERCEPTOR_NAMESPACE_HEADER]: environment.publicApiNamespace,
      },
    });
  }

  public activateAccount(token: string): Observable<void> {
    return this.post<{ token: string }>(
      `/activate-account/${token}`,
      undefined,
      {
        headers: {
          [AUTH_INTERCEPTOR_NAMESPACE_HEADER]: environment.publicApiNamespace,
        },
      },
    ).pipe(
      tap(({ token }) => this.store.save(token, { login: true })),
      map(() => undefined),
    );
  }

  public registerBusinessData(form: RegisterCompanyForm): Observable<void> {
    return this.post<Business>('/complete-business-data', form, {
      headers: {
        [AUTH_INTERCEPTOR_NAMESPACE_HEADER]: environment.publicApiNamespace,
      },
    }).pipe(
      tap((business) => this.store.setBusiness(business)),
      map(() => undefined),
    );
  }

  public registerDocuments<T extends BusinessDocumentType>(
    type: T,
    form: DocumentsForm[T],
  ): Observable<void> {
    let file: string | File | null;
    if ('ceidgAbstract' in form) {
      file = form.ceidgAbstract.file;
    } else if ('thirdPartyLiabilityInsurancePolicy' in form) {
      file = form.thirdPartyLiabilityInsurancePolicy!.file;
    } else if ('regonEntry' in form) {
      file = form.regonEntry!.file;
    } else if ('transportLicense' in form) {
      file = form.transportLicense!.file;
    } else {
      file = form.vatStatusConfirmation!.file;
    }

    return this.storageService
      .uploadFile(file as File, environment.publicApiNamespace)
      .pipe(
        map(
          (file) =>
            ({
              [type]: { ...(form as any)[type], file },
            }) as Pick<BusinessDocuments, T>,
        ),
        switchMap((document: Pick<BusinessDocuments, T>) =>
          this.post<void>('/upload-documents', document, {
            headers: {
              [AUTH_INTERCEPTOR_NAMESPACE_HEADER]:
                environment.publicApiNamespace,
            },
          }),
        ),
      );
  }

  public resetPassword(
    form: ResetPasswordForm,
    token: string,
    companyId: string | null,
  ): Observable<void> {
    return this.post(`/reset-password/${token}`, form, {
      headers: {
        [AUTH_INTERCEPTOR_NAMESPACE_HEADER]:
          companyId ?? environment.publicApiNamespace,
      },
    });
  }

  public recoverPassword(form: ForgotPasswordForm): Observable<void> {
    const { companyId, ...body } = form;

    return this.post('/recover-password', body, {
      headers: {
        [AUTH_INTERCEPTOR_NAMESPACE_HEADER]:
          companyId ?? environment.publicApiNamespace,
      },
    });
  }

  public joinCompany(
    form: JoinForm,
    companyId: string,
    token: string,
  ): Observable<void> {
    return this.put(`/employees/activate/${token}`, form, {
      headers: { [AUTH_INTERCEPTOR_NAMESPACE_HEADER]: companyId },
    });
  }

  private scheduleRefreshToken(exp: number): void {
    this.refreshToken$.next();

    const expirationTime = DateTime.fromSeconds(exp).diffNow().toMillis();
    const refreshTime = Math.min(
      Math.round(expirationTime * 0.9),
      Duration.fromObject({ day: 1 }).toMillis(),
    );

    timer(refreshTime)
      .pipe(
        switchMap(() => this.refreshToken().pipe(catchError(() => EMPTY))),
        takeUntil(this.refreshToken$),
        takeUntil(this.store.logout$),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
